import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Group,
  Text,
  Button,
  Avatar,
  Stack,
  Paper,
  Progress,
  Alert,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AlertTriangle, Check } from 'react-feather';
import { cohortBillcomApi } from '../../../../services/api/billcom';
import type { Net10PendingPayment, PaymentJob } from '../../../../services/api/billcom';
import { formatCents } from '../../../../utils/formatters';
import { getNet10SessionDetail } from '../../../../config/compensationConfig';
import { getAvatarColor, getInitialsFromParts } from '../../../../utils/avatarHelpers';

interface SubmitAllNet10ModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  payments: Net10PendingPayment[];
  onSuccess: () => void;
}

type ModalView = 'confirm' | 'progress' | 'complete';

export function SubmitAllNet10Modal({
  opened,
  onClose,
  cohortId,
  payments,
  onSuccess,
}: SubmitAllNet10ModalProps) {
  const [view, setView] = useState<ModalView>('confirm');
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState<PaymentJob | null>(null);
  // MED-1383: true once this run has refreshed the lists, so a refresh runs only once.
  const refreshedRef = useRef(false);

  // Reset state when modal opens
  useEffect(() => {
    if (opened) {
      setView('confirm');
      setJob(null);
      setSubmitting(false);
      refreshedRef.current = false;
    }
  }, [opened]);

  // MED-1383: refresh the lists as soon as the job ends. Before, they refreshed only
  // when the CM closed this dialog, so Payments Due and Sent to Bill.com stayed stale.
  useEffect(() => {
    if (view === 'complete' && !refreshedRef.current) {
      refreshedRef.current = true;
      onSuccess();
    }
  }, [view, onSuccess]);

  // Poll job progress
  const pollJob = useCallback(
    async (jobId: string) => {
      try {
        const updated = await cohortBillcomApi.getJobProgress(cohortId, jobId);
        setJob(updated);
        if (updated.status === 'in_progress') {
          setTimeout(() => pollJob(jobId), 2000);
        } else {
          setView('complete');
        }
      } catch {
        setView('complete');
      }
    },
    [cohortId],
  );

  const handleSubmitAll = async () => {
    try {
      setSubmitting(true);
      const result = await cohortBillcomApi.submitAllNet10Payments(cohortId);
      setView('progress');

      // Start polling
      const jobData = await cohortBillcomApi.getJobProgress(cohortId, result.job_id);
      setJob(jobData);
      if (jobData.status === 'in_progress') {
        setTimeout(() => pollJob(result.job_id), 2000);
      } else {
        setView('complete');
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Failed to submit payments';
      notifications.show({ title: 'Error', message: msg, color: 'red' });
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const submittablePayments = payments.filter(p => p.billcom_vendor_id);
  const totalCents = submittablePayments.reduce((sum, p) => sum + Number(p.amount_cents), 0);
  const jobProgress = job
    ? Math.round(((job.completed_count + job.failed_count) / Math.max(job.total_payments, 1)) * 100)
    : 0;

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Text fw={700} size="lg">
          {view === 'confirm' && `Submit ${submittablePayments.length} pending payments to Bill.com?`}
          {view === 'progress' && 'Submitting Payments...'}
          {view === 'complete' && 'Submission Complete'}
        </Text>
      }
      size="lg"
      centered
      closeOnClickOutside={view === 'confirm'}
      closeOnEscape={view !== 'progress'}
    >
      {/* Confirm View */}
      {view === 'confirm' && (
        <>
          <Stack gap="xs">
            {submittablePayments.map((p) => {
              const initials = getInitialsFromParts(p.coach_first_name, p.coach_last_name);
              const avatarColor = getAvatarColor(p.coach_user_id);
              return (
                <Paper
                  key={`${p.event_reference_type}-${p.event_reference_id}-${p.coach_user_id}`}
                  p="sm"
                  radius="sm"
                  style={{ background: 'var(--medley-bg-main, #F5F3EF)' }}
                >
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Avatar size="sm" radius="xl" color={avatarColor}>
                        {initials}
                      </Avatar>
                      <div>
                        <Text size="sm" fw={500}>
                          {p.coach_name}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {p.event_name}
                        </Text>
                        {/* MED-1383: which session this pays. */}
                        {getNet10SessionDetail(p) && (
                          <Text size="xs" c="dimmed">
                            {getNet10SessionDetail(p)}
                          </Text>
                        )}
                      </div>
                    </Group>
                    <Text size="sm" fw={600}>
                      {formatCents(p.amount_cents)}
                    </Text>
                  </Group>
                </Paper>
              );
            })}
          </Stack>

          <Paper
            p="sm"
            mt="xs"
            radius="sm"
            style={{ background: 'var(--medley-bg-main, #F5F3EF)', borderTop: '1px solid #E8E6E1' }}
          >
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                Total
              </Text>
              <Text size="lg" fw={700}>
                {formatCents(totalCents)}
              </Text>
            </Group>
          </Paper>

          <Group justify="flex-end" mt="lg">
            <Button variant="default" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="medleyGreen.9"
              onClick={handleSubmitAll}
              loading={submitting}
            >
              Submit All
            </Button>
          </Group>
        </>
      )}

      {/* Progress View */}
      {view === 'progress' && job && (
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Submitting {job.completed_count + job.failed_count} of {job.total_payments} payments...
          </Text>
          <Progress value={jobProgress} size="md" color="medleyGreen" radius="xl" animated />
          {job.failed_count > 0 && (
            <Text size="xs" c="dangerRed">
              {job.failed_count} failed
            </Text>
          )}
        </Stack>
      )}

      {/* Complete View */}
      {view === 'complete' && job && (
        <Stack gap="md">
          {job.failed_count === 0 ? (
            <Alert color="medleyGreen" variant="light" icon={<Check size={16} />}>
              <Text size="sm" fw={500}>
                All {job.completed_count} payments submitted successfully
              </Text>
            </Alert>
          ) : (
            <Alert color="dangerRed" variant="light" icon={<AlertTriangle size={16} />}>
              <Text size="sm" fw={500}>
                {job.completed_count} succeeded, {job.failed_count} failed
              </Text>
              {job.errors.length > 0 && (
                <Stack gap={4} mt="xs">
                  {job.errors.map((err, i) => (
                    <Text key={i} size="xs" c="dimmed">
                      {err.error}
                    </Text>
                  ))}
                </Stack>
              )}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button color="medleyGreen.9" onClick={handleClose}>
              Done
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
